import { useEffect, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent, PointerEvent as ReactPointerEvent } from "react";
import { Button } from "../../../components/ui/Button";
import type { TopBarSpec } from "../../../components/TopBar";
import { useT } from "../../../i18n";
import type { ConsoleApi } from "../api/consoleApi";
import type { ConsoleRoom, SandboxObject, SandboxPrefab, SandboxDraft } from "../types";
import { CmdRejected } from "../api/consoleApi";
import { machinePos } from "../lib/camera";
import {
  add,
  clearAll,
  draftOf,
  FLOOR,
  freeSpot,
  INVENTORY,
  move,
  payload,
  PREVIEW,
  readObjects,
  remove,
  rotate,
  SANDBOX_MAX,
  sameLayout,
  sizeOf,
  SNAP_M,
} from "../lib/sandbox";
import { useBoardConnection } from "../hooks/useBoardConnection";
import { ItemPreview } from "../../course";
import type { InventoryCat } from "../lib/sandbox";
import {
  addPiece,
  canAdd,
  DECOR_CATS,
  DECOR_KINDS,
  decorPayload,
  decorPreview,
  movePiece,
  readDecor,
  removePiece,
  rotatePiece,
  sameDecor,
} from "../lib/sandboxDecor";
import type { DecorCat, DecorPiece } from "../lib/sandboxDecor";
import { useCourseApi } from "../../../lib/api/courseApi";
import { useStageModels } from "../../stage";

/** 인벤토리 묶음 — 놀이 물건(물리) 3 + 꾸밈(보기 전용 · 운영 무대 꾸밈 인벤토리와 같은 품목) 4 */
type Cat = InventoryCat | DecorCat;
type Pick = { cat: Cat; key: string };
const isDecor = (c: Cat): c is DecorCat => (DECOR_CATS as readonly string[]).includes(c);
const isDecorId = (id: string) => id.startsWith("d");
/** 바닥 평면에서 꾸밈 표시 크기(m) — 작은 손님도 누를 수 있게 */
const DECOR_MARK_M = 0.8;

/** 바닥 평면에 기계 자리를 다시 그리는 간격 */
const MACHINES_MS = 300;
/** 끌기로 보는 최소 이동(px) — 그보다 작으면 누르기 */
const DRAG_PX = 6;

/** 화면 좌표 → 바닥(x · z m) — 위쪽이 +z(교실 앞) · 크기가 0 이면(시험 · 숨김) 가운데 */
function toFloor(clientX: number, clientY: number, r: DOMRect): { x: number; z: number } {
  if (!r.width || !r.height) return { x: 0, z: FLOOR.cz };
  const u = (clientX - r.left) / r.width;
  const v = (clientY - r.top) / r.height;
  return { x: -FLOOR.x / 2 + u * FLOOR.x, z: FLOOR.cz + FLOOR.z / 2 - v * FLOOR.z };
}
const pctX = (x: number) => ((x + FLOOR.x / 2) / FLOOR.x) * 100;
const pctZ = (z: number) => ((FLOOR.cz + FLOOR.z / 2 - z) / FLOOR.z) * 100;

/**
 * FS-07-01 자유 놀이터 — 교사 배치 도구(SCR v0.6 sfs-07 오른쪽 '교사 배치 도구' · SR-MODE-06) — 콘솔 탭(/teacher/room/:roomId/sandbox).
 *  - 왼쪽: 위에서 본 바닥(24 × 20 m · 0.5 m 격자) — 지금 기계 자리(칠판 세션으로 받아 0.3초마다)와 초안 물건
 *    (3D 바닥 레이캐스트 대신 평면: SwiftShader·저사양 교실 PC 에서도 가볍고, 격자 맞춤·키보드 조작·시험이 쉽다)
 *  - 오른쪽: 인벤토리(2026-10-11 — 운영 무대 꾸밈 인벤토리와 같은 틀) — 묶음 탭(굴러가요 · 쌓아요 · 길 · 장치 · 꾸밈: 소품 · 캐릭터 · 물건 · 올린 모델) → 물건 → 3D 미리보기(빙글 · 크기) →
 *    '＋ 놓기'(바닥 가운데 근처 빈자리). 물건을 눌러 고른 뒤 바닥을 누르거나, 바닥으로 끌어 놓아도 된다(예전과 같다)
 *  - 물건: 끌어 옮기기 · 눌러 고르기 → ⟳ 돌리기 · 🗑 삭제(키보드: 방향키 0.5 m · R · Delete) · 🗑 전체 초기화 · 물건 n/50
 *  - '✓ 배치 끝' = 초안을 한 번에 적용(POST …/layout — 월드 다시 싣기 · 기계는 자리로) · 자유 놀이터가 아니면 모드 바꾸기 안내
 *  - 꾸밈(소품 · 캐릭터 · 물건 · 올린 모델)은 보기 전용 — 기계가 지나가고 물리 · 판정 · 리플레이 해시와 무관(룸 서버가 meta.mode.decor 로 실어 나른다).
 *    운영 무대 꾸밈 인벤토리와 같은 품목 · 올린 모델은 카탈로그 stage_model(운영자가 올린 것)에서.
 * 학생은 배치·삭제를 못 한다(학생 화면 FS-07-01 은 '배치·삭제는 선생님만').
 */
export function SandboxPanel({
  room,
  api,
  onTop,
  onError,
  onMode,
  onDone,
}: {
  room: ConsoleRoom;
  api: ConsoleApi;
  onTop: (spec: TopBarSpec | null) => void;
  onError: (e: unknown) => void;
  onMode: (modeId: string) => void;
  onDone: (text: string) => void;
}) {
  const t = useT();
  const conn = useBoardConnection(api);
  const meta = conn?.meta ?? null;
  const inSandbox = room.modeId === "sandbox";
  const applied = meta?.mode.kind === "sandbox" ? readObjects(meta.mode.objects) : null;
  const appliedKey = applied ? JSON.stringify(applied) : null;
  const [draft, setDraft] = useState<SandboxDraft | null>(null);
  const [base, setBase] = useState<SandboxObject[] | null>(null);
  const [armed, setArmed] = useState<Pick | null>(null);
  // 인벤토리 — 고른 묶음 · 미리보기 물건(누르면 바닥 놓기도 켜진다 · armed)
  const [cat, setCat] = useState<Cat>(INVENTORY[0]!.cat);
  const [kind, setKind] = useState<string>(INVENTORY[0]!.kinds[0]!);
  // 꾸밈 초안(보기 전용) · 지금 적용된 꾸밈
  const appliedDecor = meta?.mode.kind === "sandbox" ? readDecor(meta.mode.decor) : null;
  const appliedDecorKey = appliedDecor ? JSON.stringify(appliedDecor) : null;
  const [decor, setDecor] = useState<readonly DecorPiece[] | null>(null);
  const [decorBase, setDecorBase] = useState<readonly DecorPiece[]>([]);
  const stageModels = useStageModels(useCourseApi());
  const [modelUrl, setModelUrl] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [rejected, setRejected] = useState<string | null>(null);
  const [machines, setMachines] = useState<
    { machine: number; name: string; x: number; z: number }[]
  >([]);
  const plane = useRef<HTMLDivElement>(null);
  const dropped = useRef(false);
  const [ghost, setGhost] = useState<{ label: string; x: number; y: number } | null>(null);
  const drag = useRef<{
    kind: "new" | "move";
    pick?: Pick;
    id?: string;
    x0: number;
    y0: number;
    moved: boolean;
  } | null>(null);

  // 지금 배치(서버)를 받으면 — 초안이 없거나 손대지 않았으면 그것으로 시작
  useEffect(() => {
    if (!applied) return;
    setBase(applied);
    setDraft((d) => (!d || (base && sameLayout(d.objects, base)) ? draftOf(applied) : d));
    // 서버 배치가 바뀔 때만
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedKey]);
  // 꾸밈도 같은 규칙 — 손대지 않았으면 서버 꾸밈으로
  useEffect(() => {
    if (!appliedDecor) return;
    setDecorBase(appliedDecor);
    setDecor((d) => (!d || sameDecor(d, decorBase) ? appliedDecor : d));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedDecorKey]);
  // 올린 모델 미리보기 — 고른 모델 GLB 를 받아서
  const modelUrlOf = stageModels.url;
  useEffect(() => {
    let alive = true;
    setModelUrl(null);
    if (cat === "models" && modelUrlOf && kind)
      void modelUrlOf(kind).then((u) => alive && setModelUrl(u));
    return () => {
      alive = false;
    };
  }, [cat, kind, modelUrlOf]);
  // 룸이 아직 자유 놀이터가 아니면 빈 초안으로 시작(모드를 바꾸면 위에서 서버 배치로)
  useEffect(() => {
    if (!inSandbox && !draft) setDraft(draftOf([]));
  }, [inSandbox, draft]);

  // 바닥에 기계 자리
  useEffect(() => {
    if (!conn || !meta) return;
    const names = new Map(conn.members.map((m) => [m.machine, m.name]));
    const read = () => {
      const f = conn.frame();
      if (!f) return;
      setMachines(
        meta.machines.flatMap((m) => {
          const p = machinePos(meta, f.transforms, m.machine);
          return p ? [{ machine: m.machine, name: names.get(m.machine) ?? "", ...p }] : [];
        }),
      );
    };
    read();
    const h = window.setInterval(read, MACHINES_MS);
    return () => window.clearInterval(h);
  }, [conn, meta]);

  const objects = draft?.objects ?? [];
  const pieces = decor ?? [];
  const dirty =
    !!draft && (!sameLayout(draft.objects, base ?? []) || !sameDecor(pieces, decorBase));
  const full = objects.length >= SANDBOX_MAX;
  const sel = objects.find((o) => o.id === selected) ?? null;
  const selDecor = pieces.find((p) => p.id === selected) ?? null;

  const objName = (k: SandboxPrefab) => t(`sandbox.prefab.${k}`);
  const name = (c: Cat, k: string) =>
    !isDecor(c)
      ? t(`sandbox.prefab.${k}`)
      : c === "models"
        ? (stageModels.models.get(k)?.name ?? k)
        : t(`ops.stages.kind.${k}`);
  const kindsOf = (c: Cat): readonly string[] =>
    !isDecor(c)
      ? INVENTORY.find((g) => g.cat === c)!.kinds
      : c === "models"
        ? [...stageModels.models.keys()]
        : DECOR_KINDS[c];
  const canPlace = (p: Pick) => (isDecor(p.cat) ? canAdd(pieces, p.cat) : !full);

  const topKey = JSON.stringify([objects.length, pieces.length, dirty, inSandbox]);
  useEffect(() => {
    onTop({
      icon: "🧸",
      title: t("sandbox.topTitle"),
      chips: [
        {
          label: t("sandbox.count", { n: objects.length, max: SANDBOX_MAX }),
          testId: "sandbox-count",
        },
        ...(pieces.length
          ? [
              {
                label: t("sandbox.decorCount", { n: pieces.length }),
                testId: "sandbox-decor-count",
              },
            ]
          : []),
        {
          label: t(dirty ? "sandbox.stateDraft" : "sandbox.stateApplied"),
          testId: "sandbox-state",
        },
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topKey, onTop, t]);
  useEffect(() => () => onTop(null), [onTop]);

  const put = (d: SandboxDraft) => {
    setDraft(d);
    setRejected(null);
  };
  /** 바닥 자리(m)에 놓기 — 놀이 물건은 초안 · 꾸밈은 꾸밈 초안 · 놓은 것을 고른다 */
  const placeAt = (p: Pick, x: number, z: number) => {
    if (!draft) return;
    if (isDecor(p.cat)) {
      const next = addPiece(pieces, p.cat, p.key, x, z);
      if (next === pieces) return;
      setDecor(next);
      setRejected(null);
      setSelected(next[next.length - 1]!.id);
      return;
    }
    const next = add(draft, p.key as SandboxPrefab, x, z);
    if (next !== draft) {
      put(next);
      setSelected(next.objects[next.objects.length - 1]!.id);
    }
  };
  const place = (p: Pick, clientX: number, clientY: number) => {
    if (!plane.current) return;
    const f = toFloor(clientX, clientY, plane.current.getBoundingClientRect());
    placeAt(p, f.x, f.z);
  };
  /** ＋ 놓기 — 가운데 근처 빈자리(물건 · 꾸밈 모두 피해서) */
  const placeFree = (p: Pick) => {
    if (!draft) return;
    const taken = {
      ...draft,
      objects: [
        ...draft.objects,
        ...pieces.map((d) => ({
          id: d.id,
          prefab: "crate" as const,
          at: [d.at[0], 0, d.at[1]] as [number, number, number],
        })),
      ],
    };
    const at = freeSpot(taken, isDecor(p.cat) ? "crate" : (p.key as SandboxPrefab));
    placeAt(p, at.x, at.z);
  };
  /** 고른 것(물건 · 꾸밈) 옮기기 · 돌리기 · 지우기 */
  const moveAny = (id: string, x: number, z: number) => {
    if (isDecorId(id)) {
      setDecor(movePiece(pieces, id, x, z));
      setRejected(null);
    } else if (draft) put(move(draft, id, x, z));
  };
  const rotateAny = (id: string) => {
    if (isDecorId(id)) setDecor(rotatePiece(pieces, id));
    else if (draft) put(rotate(draft, id));
  };
  const removeAny = (id: string) => {
    if (isDecorId(id)) setDecor(removePiece(pieces, id));
    else if (draft) put(remove(draft, id));
    setSelected(null);
  };

  // 팔레트 — 누르면 고르기(한 번 더 누르면 풀기) · 바닥으로 끌면 놓기
  const onPaletteDown = (pick: Pick) => (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (!canPlace(pick)) return;
    drag.current = { kind: "new", pick, x0: e.clientX, y0: e.clientY, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPaletteMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d || d.kind !== "new") return;
    if (!d.moved && Math.hypot(e.clientX - d.x0, e.clientY - d.y0) < DRAG_PX) return;
    d.moved = true;
    setGhost({ label: name(d.pick!.cat, d.pick!.key), x: e.clientX, y: e.clientY });
  };
  const onPaletteUp = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    drag.current = null;
    setGhost(null);
    if (!d || d.kind !== "new") return;
    if (!d.moved) return; // 누르기는 click 이 처리
    dropped.current = true;
    const r = plane.current?.getBoundingClientRect();
    if (
      r &&
      e.clientX >= r.left &&
      e.clientX <= r.right &&
      e.clientY >= r.top &&
      e.clientY <= r.bottom
    )
      place(d.pick!, e.clientX, e.clientY);
  };

  // 물건 — 끌어 옮기기
  const onObjDown = (id: string) => (e: ReactPointerEvent<HTMLButtonElement>) => {
    setSelected(id);
    drag.current = { kind: "move", id, x0: e.clientX, y0: e.clientY, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.stopPropagation();
  };
  const onObjMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d || d.kind !== "move" || !draft || !plane.current) return;
    if (!d.moved && Math.hypot(e.clientX - d.x0, e.clientY - d.y0) < DRAG_PX) return;
    d.moved = true;
    const p = toFloor(e.clientX, e.clientY, plane.current.getBoundingClientRect());
    moveAny(d.id!, p.x, p.z);
  };
  const onObjUp = () => {
    drag.current = null;
  };
  const onObjKey =
    (id: string, at: readonly [number, number]) => (e: KeyboardEvent<HTMLButtonElement>) => {
      if (!draft) return;
      const step: Record<string, [number, number]> = {
        ArrowLeft: [-SNAP_M, 0],
        ArrowRight: [SNAP_M, 0],
        ArrowUp: [0, SNAP_M],
        ArrowDown: [0, -SNAP_M],
      };
      const s = step[e.key];
      if (s) {
        e.preventDefault();
        moveAny(id, at[0] + s[0], at[1] + s[1]);
      } else if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        removeAny(id);
      } else if (e.key === "r" || e.key === "R") {
        e.preventDefault();
        rotateAny(id);
      }
    };

  const apply = async () => {
    if (!draft) return;
    setBusy(true);
    setRejected(null);
    try {
      await api.layout(
        payload(draft),
        decorPayload(pieces, (k) => stageModels.models.get(k)?.height),
      );
      setBase(draft.objects.map((o) => ({ ...o })));
      setDecorBase(pieces);
      onDone(t("sandbox.appliedToast", { n: draft.objects.length }));
    } catch (e) {
      if (e instanceof CmdRejected) setRejected(e.field);
      onError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pf-sandbox" data-testid="fs-07-01-teacher">
      {!inSandbox && (
        <div
          className="pf-banner pf-banner--info pf-sandbox__mode"
          role="note"
          data-testid="sandbox-not-mode"
        >
          <p className="pf-banner__text">{t("sandbox.notMode")}</p>
          <Button
            variant="green"
            className="pf-btn--sm"
            data-testid="sandbox-switch"
            disabled={room.state === "prewarm"}
            onClick={() => onMode("sandbox")}
          >
            {t("sandbox.switchMode")}
          </Button>
        </div>
      )}
      <div className="pf-sandbox__grid">
        <div className="pf-sandbox__scene">
          <div
            ref={plane}
            className={`pf-sandbox__plane${armed ? " pf-sandbox__plane--armed" : ""}`}
            role="application"
            aria-label={t("sandbox.planeLabel")}
            aria-describedby="sandbox-hint"
            data-testid="sandbox-plane"
            onClick={(e) => {
              if (armed && canPlace(armed)) place(armed, e.clientX, e.clientY);
              else setSelected(null);
            }}
          >
            {machines.map((m) => (
              <span
                key={`m${m.machine}`}
                className="pf-sandbox__machine"
                style={{ left: `${pctX(m.x)}%`, top: `${pctZ(m.z)}%` }}
                data-machine={m.machine}
                title={m.name}
                aria-hidden="true"
              />
            ))}
            {objects.map((o) => {
              const [w, d] = sizeOf(o);
              return (
                <button
                  key={o.id}
                  type="button"
                  className="pf-sandbox__obj"
                  data-prefab={o.prefab}
                  data-id={o.id}
                  aria-pressed={o.id === selected}
                  aria-label={t("sandbox.objLabel", {
                    name: objName(o.prefab),
                    x: o.at[0],
                    z: o.at[2],
                  })}
                  style={
                    {
                      left: `${pctX(o.at[0])}%`,
                      top: `${pctZ(o.at[2])}%`,
                      // 실제 크기(바닥 비율) — 작은 물건도 누를 수 있게 CSS 가 최소 크기를 둔다
                      "--pf-obj-w": `${(w / FLOOR.x) * 100}%`,
                      "--pf-obj-d": `${(d / FLOOR.z) * 100}%`,
                    } as CSSProperties
                  }
                  onPointerDown={onObjDown(o.id)}
                  onPointerMove={onObjMove}
                  onPointerUp={onObjUp}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelected(o.id);
                  }}
                  onKeyDown={onObjKey(o.id, [o.at[0], o.at[2]])}
                >
                  {objName(o.prefab)}
                </button>
              );
            })}
            {pieces.map((d) => (
              // 꾸밈(보기 전용) — 점선 표시 · 고르기 · 끌어 옮기기 · 방향키 · R · Delete(물건과 같다)
              <button
                key={d.id}
                type="button"
                className="pf-sandbox__decor"
                data-decor={d.cat}
                data-id={d.id}
                aria-pressed={d.id === selected}
                aria-label={t("sandbox.decorLabel", {
                  name: name(d.cat, d.key),
                  x: d.at[0],
                  z: d.at[1],
                })}
                style={
                  {
                    left: `${pctX(d.at[0])}%`,
                    top: `${pctZ(d.at[1])}%`,
                    "--pf-obj-w": `${(DECOR_MARK_M / FLOOR.x) * 100}%`,
                    "--pf-obj-d": `${(DECOR_MARK_M / FLOOR.z) * 100}%`,
                  } as CSSProperties
                }
                onPointerDown={onObjDown(d.id)}
                onPointerMove={onObjMove}
                onPointerUp={onObjUp}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelected(d.id);
                }}
                onKeyDown={onObjKey(d.id, d.at)}
              >
                {name(d.cat, d.key)}
              </button>
            ))}
          </div>
          <p className="pf-sandbox__legend pf-muted">{t("sandbox.legend")}</p>
        </div>
        <section
          className="pf-pane pf-pane--hero pf-sandbox__tools"
          aria-labelledby="sandbox-tools-title"
        >
          <h2 id="sandbox-tools-title" className="pf-h3">
            {t("sandbox.tools")}
          </h2>
          <fieldset className="pf-ops-stages__box pf-sandbox__inv" data-testid="sandbox-inventory">
            <legend>{t("sandbox.inventory")}</legend>
            <div className="pf-seg pf-seg--pill" role="tablist" aria-label={t("sandbox.inventory")}>
              {[...INVENTORY.map((g) => g.cat), ...DECOR_CATS].map((c) => (
                <button
                  key={c}
                  type="button"
                  role="tab"
                  aria-selected={c === cat}
                  className={`pf-ops-type${c === cat ? " pf-ops-type--on" : ""}${isDecor(c) ? " pf-sandbox__cat--decor" : ""}`}
                  data-cat={c}
                  onClick={() => {
                    setCat(c);
                    setKind(kindsOf(c)[0] ?? "");
                    setArmed(null);
                  }}
                >
                  {t(
                    isDecor(c)
                      ? `ops.stages.cat${c[0]!.toUpperCase()}${c.slice(1)}`
                      : `sandbox.cat.${c}`,
                  )}
                </button>
              ))}
            </div>
            <div
              className="pf-sandbox__palette"
              role="group"
              aria-label={t("sandbox.palette")}
              data-testid="sandbox-palette"
            >
              {cat === "models" && kindsOf(cat).length === 0 && (
                <span className="pf-muted" data-testid="sandbox-no-models">
                  {t("sandbox.noModels")}
                </span>
              )}
              {kindsOf(cat).map((k) => (
                <button
                  key={k}
                  type="button"
                  className={`pf-ops-stages__kind pf-sandbox__tool${k === kind ? " is-on" : ""}`}
                  aria-pressed={armed?.cat === cat && armed.key === k}
                  disabled={!canPlace({ cat, key: k })}
                  {...(isDecor(cat) ? { "data-decor": k } : { "data-prefab": k })}
                  onPointerDown={onPaletteDown({ cat, key: k })}
                  onPointerMove={onPaletteMove}
                  onPointerUp={onPaletteUp}
                  onClick={() => {
                    // 끌어 놓은 뒤 따라오는 click 은 고르기로 치지 않는다
                    if (dropped.current) {
                      dropped.current = false;
                      return;
                    }
                    setKind(k);
                    setArmed((a) => (a?.cat === cat && a.key === k ? null : { cat, key: k }));
                  }}
                >
                  {name(cat, k)}
                </button>
              ))}
            </div>
            <ItemPreview
              item={
                isDecor(cat)
                  ? kind
                    ? decorPreview(cat, kind, modelUrl)
                    : null
                  : { env: PREVIEW[kind as SandboxPrefab] }
              }
              label={t("sandbox.itemPreview", { name: kind ? name(cat, kind) : "" })}
              failText={t("ops.stages.previewFail")}
              testId="sandbox-item-preview"
            />
            {/* 크기는 규격 값 — 3D 를 그릴 수 없는 기기에서도 보인다 · 꾸밈은 보기 전용 안내 */}
            {kind && (
              <p className="pf-muted" data-testid="sandbox-item-size">
                {isDecor(cat)
                  ? t("sandbox.decorNote", { name: name(cat, kind) })
                  : t("sandbox.itemSize", {
                      name: name(cat, kind),
                      x: PREVIEW[kind as SandboxPrefab].size[0],
                      y: PREVIEW[kind as SandboxPrefab].size[1],
                      z: PREVIEW[kind as SandboxPrefab].size[2],
                    })}
              </p>
            )}
            <Button
              className="pf-btn--sm"
              disabled={!kind || !draft || !canPlace({ cat, key: kind })}
              data-testid="sandbox-place"
              onClick={() => kind && placeFree({ cat, key: kind })}
            >
              {t("sandbox.place")}
            </Button>
          </fieldset>
          <p className="pf-muted" id="sandbox-hint">
            {t("sandbox.hint", { max: SANDBOX_MAX })}
          </p>
          <div
            className="pf-progress"
            role="progressbar"
            aria-label={t("sandbox.gauge")}
            aria-valuemin={0}
            aria-valuemax={SANDBOX_MAX}
            aria-valuenow={objects.length}
            data-testid="sandbox-gauge"
          >
            <i
              className="pf-progress__fill"
              style={{ width: `${(objects.length / SANDBOX_MAX) * 100}%` }}
            />
          </div>
          <p className="pf-muted" aria-hidden="true">
            {t("sandbox.count", { n: objects.length, max: SANDBOX_MAX })}
            {full && ` · ${t("sandbox.full")}`}
          </p>
          {(sel || selDecor) && (
            <div className="pf-row pf-row--wrap pf-sandbox__sel" data-testid="sandbox-selected">
              <span className="pf-muted">
                {sel
                  ? t("sandbox.selected", {
                      name: objName(sel.prefab),
                      x: sel.at[0],
                      z: sel.at[2],
                    })
                  : t("sandbox.selectedDecor", {
                      name: name(selDecor!.cat, selDecor!.key),
                      x: selDecor!.at[0],
                      z: selDecor!.at[1],
                    })}
              </span>
              <Button
                variant="ghost"
                className="pf-btn--sm"
                data-testid="sandbox-rotate"
                onClick={() => rotateAny((sel ?? selDecor)!.id)}
              >
                {t("sandbox.rotate")}
              </Button>
              <Button
                variant="ghost"
                className="pf-btn--sm"
                data-testid="sandbox-delete"
                onClick={() => removeAny((sel ?? selDecor)!.id)}
              >
                {t("sandbox.delete")}
              </Button>
            </div>
          )}
          {rejected && (
            <p className="pf-field-err" role="alert" data-testid="sandbox-rejected">
              {t(rejected === "mode" ? "sandbox.rejectedMode" : "sandbox.rejected", {
                field: rejected,
              })}
            </p>
          )}
          <div className="pf-row pf-sandbox__actions">
            <Button
              variant="ghost"
              className="pf-btn--sm"
              disabled={(!objects.length && !pieces.length) || busy}
              data-testid="sandbox-clear"
              onClick={() => {
                if (!draft) return;
                put(clearAll(draft));
                setDecor([]);
                setSelected(null);
              }}
            >
              {t("sandbox.clear")}
            </Button>
            <Button
              variant="green"
              className="pf-btn--sm"
              disabled={!inSandbox || !dirty || busy}
              data-testid="sandbox-apply"
              onClick={() => void apply()}
            >
              {t("sandbox.apply")}
            </Button>
          </div>
        </section>
      </div>
      {ghost && (
        <span
          className="pf-sandbox__ghost"
          style={{ left: ghost.x, top: ghost.y }}
          aria-hidden="true"
        >
          {ghost.label}
        </span>
      )}
    </div>
  );
}
